import type { Definition } from 'typescript-json-schema';
import { SchemaTag } from '@sap/ux-specification-types';
import type { DecoratorContext, DecoratorStep } from './interfaces';
import { metadataKeys } from './decorators';
import { Enums, Message, Hide } from './steps';

/**
 * Default steps executed in order.
 * Order: enums -> message -> hide
 * Validity is opt-in - add new Validity() to steps to enable it.
 * TODO: add @validity for general enablement
 */
const DEFAULT_STEPS: DecoratorStep[] = [new Enums(), new Message(), new Hide()];

type DecoratorClass = new (...args: unknown[]) => object;

/**
 * Options for configuring the Decorator processor.
 */
export interface DecoratorOptions {
    /** Custom steps (replaces defaults entirely) */
    steps?: DecoratorStep[];
}

/**
 * Decorator processor - applies decorator metadata to schema definitions.
 *
 * This class processes decorator annotations (@hide, @message, @enums, @validity)
 * through a configurable pipeline of steps.
 *
 * If the target class defines an `init(context: DecoratorContext)` method, it will be
 * called automatically after all steps have been executed.
 *
 * @example
 * ```typescript
 * // Define a class with decorators (no base class needed)
 * class TableSettings implements v4.TableSettings {
 *     @hide(when(tableContext.type, 'AnalyticalTable'))
 *     @validity({ since: '1.84.0' })
 *     creationMode?: v4.TableCreationModeLR;
 * }
 *
 * // Create decorator processor with context
 * const decorator = new Decorator({ schema: appSchema, app, page });
 *
 * // Apply decorators - just pass the class
 * const definition = decorator.apply(TableSettings, { type: 'GridTable' });
 *
 * // Apply with an explicit definition (e.g., view-specific copy)
 * const definition = decorator.apply(Table, customContext, tableDefinition);
 * ```
 */
export class Decorator {
    private readonly context: DecoratorContext;
    private readonly steps: DecoratorStep[];

    /**
     * Creates a new Decorator processor.
     *
     * @param context - Runtime context for decorator evaluation (app, page, schema)
     * @param options - Optional configuration for steps
     */
    constructor(context: DecoratorContext, options?: DecoratorOptions) {
        this.context = context;
        this.steps = options?.steps ?? DEFAULT_STEPS;
    }

    /**
     * Initialize all decorator metadata to a schema definition.
     *
     * Resolves the definition by class name from the schema, or uses an explicit
     * definition if provided. Runs all configured steps in order, then calls
     * `init(context)` on the target class if it defines one.
     *
     * @param Target - Class constructor with decorator metadata
     * @param customContext - Optional runtime context (e.g., table state, section state)
     * @param definition - Optional explicit definition (overrides schema lookup by class name)
     * @returns The modified schema definition, or undefined if not found
     */
    init<T extends DecoratorClass>(
        Target: T | T[],
        customContext?: Record<string, unknown>,
        definition?: Definition
    ): Definition | undefined {
        if (Array.isArray(Target)) {
            for (const SingleTarget of Target) {
                this.apply(SingleTarget, customContext, definition);
            }
        } else {
            return this.apply(Target, customContext, definition);
        }
    }

    /**
     * Apply decorator metadata to a schema definition.
     *
     * Resolves the definition by class name from the schema, or uses an explicit
     * definition if provided. Runs all configured steps in order, then calls
     * `init(context)` on the target class if it defines one.
     *
     * @param Target - Class constructor with decorator metadata
     * @param customContext - Optional runtime context (e.g., table state, section state)
     * @param definition - Optional explicit definition (overrides schema lookup by class name)
     * @returns The modified schema definition, or undefined if not found
     */
    private apply<T extends DecoratorClass>(
        Target: T,
        customContext?: Record<string, unknown>,
        definition?: Definition
    ): Definition | undefined {
        // Resolve definition: explicit parameter takes precedence, then schema lookup by class name
        const resolvedDefinition = definition ?? this.context.schema?.getDefinition(Target.name);

        if (!resolvedDefinition) {
            return undefined;
        }

        // Class-level decorators (inline, not steps)
        this.applyDescription(Target, resolvedDefinition);
        this.applyIsViewNode(Target, resolvedDefinition);

        // Build runtime context with custom context merged
        const context: DecoratorContext = {
            ...this.context,
            custom: customContext
        };

        // Run steps in order (using Target.prototype for metadata lookup)
        for (const step of this.steps) {
            step.init(Target.prototype, resolvedDefinition, context);
        }

        // Auto-call init() if the target class defines a single-argument post-processing hook.
        // Guards against DecoratorStep classes (whose init takes 3 args) being invoked incorrectly.
        if (typeof Target.prototype.init === 'function' && Target.prototype.init.length === 1) {
            Target.prototype.init(context);
        }

        return resolvedDefinition;
    }

    /**
     * Apply description decorator from class metadata.
     *
     * @param Target - Class constructor with decorator metadata
     * @param definition - The schema definition to modify
     */
    private applyDescription<T extends DecoratorClass>(Target: T, definition: Definition): void {
        const description = Reflect.getMetadata(metadataKeys.description, Target);
        if (description !== undefined) {
            definition.description = description;
        }
    }

    /**
     * Apply isViewNode decorator from class metadata.
     *
     * @param Target - Class constructor with decorator metadata
     * @param definition - The schema definition to modify
     */
    private applyIsViewNode<T extends DecoratorClass>(Target: T, definition: Definition): void {
        const isViewNode = Reflect.getMetadata(metadataKeys.isViewNode, Target);
        if (isViewNode !== undefined) {
            definition[SchemaTag.isViewNode] = isViewNode;
            definition.properties = definition.properties || {};
        }
    }
}
